<template>
  <div class="space-y-4">
    <base-card title="Informasi Pengemasan" description="Detail informasi pengemasan">
      <template #action>
        <base-badge>{{ type === 'order' ? order.data.attributes.warehouse_status : (type === 'reimbursement' ? order.data.attributes.reimbursement_status_label : order.data.attributes.free_reimbursement_status_label) }}</base-badge>
      </template>

      <div class="grid grid-cols-3 gap-6 border-t pt-5">
        <div>
          <dt class="text-xs text-gray-700">No Penjualan</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            <button class="font-bold text-gray-900 inline-flex items-center gap-1" @click="detailModalVisible = true">
              {{ order.data.attributes.origin_code ?? '-' }}

              <Icon icon="heroicons:arrow-top-right-on-square" />
            </button>
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-700">Tanggal</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            <order-date-label :order="order.data" />
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-700">Kode {{ officeTypeLabel }}</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            {{ office ? office.attributes.code : '-' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-700">Nama {{ officeTypeLabel }}</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            {{ office ? office.attributes.name : '-' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-700">Alamat {{ officeTypeLabel }}</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            {{ office ? office.attributes.full_address : '-' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-700">Nomor Telepon {{ officeTypeLabel }}</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            {{ office ? office.attributes.phone : '-' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-700">Metode Pengiriman</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            {{ order.data.attributes.order_shipment === 'pickup' ? 'Diambil' : 'Dikirim' }}
          </dd>
        </div>
        <div>
          <dt class="text-xs text-gray-700">Tipe Faktur</dt>
          <dd class="mt-1 text-sm font-bold text-gray-900">
            {{ getTypeLabel }}
          </dd>
        </div>
      </div>
    </base-card>

    <base-card v-if="type === 'order' && order.data.attributes.order_shipment === 'delivery' && order.data.attributes.complete_shipment_address" :title="`Alamat ${order.data.attributes.order_shipment === 'pickup' ? 'Pengambilan' : 'Pengiriman'}`" :description="`Detail alamat ${order.data.attributes.order_shipment === 'pickup' ? 'pengambilan' : 'pengiriman'}`">
      <base-input inset disabled label="Alamat">
        {{ order.data.attributes.receipt_name || destinationOffice.attributes.name }},
        {{ order.data.attributes.receipt_phone || destinationOffice.attributes.phone }},
        {{ order.data.attributes.complete_shipment_address }}
      </base-input>
    </base-card>

    <view-order-modal
      :with-payment="false"
      :order-detail-columns="['code', 'qty', 'price', 'total_price', 'packed_qty', 'unpacked_qty']"
      :columns="[
        'code',
        'warehouse',
        'office',
        'date',
        'user',
        'area',
        'shipment_type',
        'address',
      ]"
      readonly
      :label="label"
      :visible="detailModalVisible"
      :order-id="order.data.id"
      :order-type="type"
      @close="detailModalVisible = false"
    />
  </div>
</template>

<script>
import OrderDateLabel from '@/components/order/order-date-label.vue';
import ViewOrderModal from '../order/view-order-modal.vue';

export default {
  components: {
    OrderDateLabel,
    ViewOrderModal
  },
  props: {
    officeType: {
      type: String,
      default: 'stockist',
    },
    displayOrigin: Boolean,
    order: Object,
    type: {
      type: String,
      default: 'order'
    },
    label: String
  },
  data() {
    return {
      detailModalVisible: false
    }
  },
  computed: {
    deliveryAddress() {
      const shipmentAddres = this.order.data.attributes.shipment_address;

      if (!shipmentAddres) {
        return null;
      }

      const villageRel = this.order.data.relationships.village.data;
      const districtRel = this.order.data.relationships.district.data;
      const cityRel = this.order.data.relationships.city.data;
      const provinceRel = this.order.data.relationships.province.data;

      const village = !villageRel
        ? null
        : this.getSingleIncluded(
            this.order,
            this.order.data.relationships.village.data.id
          );
      const district = !districtRel
        ? null
        : this.getSingleIncluded(
            this.order,
            this.order.data.relationships.district.data.id
          );
      const city = !cityRel
        ? null
        : this.getSingleIncluded(
            this.order,
            this.order.data.relationships.city.data.id
          );
      const province = !provinceRel
        ? null
        : this.getSingleIncluded(
            this.order,
            this.order.data.relationships.province.data.id
          );

      return [
        shipmentAddres,
        village ? village.attributes.name : '',
        district ? district.attributes.name : '',
        city ? city.attributes.name : '',
        province ? province.attributes.name : '',
      ].join(', ');
    },
    destinationOffice() {
      const destinationOffice =
        this.order.data.relationships['destination-office'].data;

      if (!destinationOffice) {
        return null;
      }

      return this.getSingleIncluded(this.order, destinationOffice.id);
    },
    originOffice() {
      const originOffice = this.order.data.relationships['origin-office'].data;

      if (!originOffice) {
        return null;
      }

      return this.getSingleIncluded(this.order, originOffice.id);
    },
    office() {
      return this.displayOrigin || this.type === 'reimbursement' ? this.originOffice : this.destinationOffice;
    },
    officeTypeLabel() {
      return {
        stockist: 'Stockist',
        stockist_center: 'Stockist Center',
        supplier: 'Supplier',
        kantor_pelayanan: 'Kantor Pelayanan',
      }[this.type === 'order' ? this.destinationOffice.attributes.office_type : this.originOffice.attributes.office_type];
    },
    getTypeLabel() {
      if (this.type === 'reimbursement') {
        return 'Reimbursement'
      }

      if (this.type === 'free_reimbursement') {
        return 'Susulan Promo'
      }

      if (this.order.data.attributes.is_transfer_promo || this.order.data.attributes.is_transfer_promo_regular) {
        return 'Kirim Promo'
      }

      if (['stockist_center', 'kantor_pelayanan'].includes(this.destinationOffice.attributes.office_type)) {
        return 'Restock'
      }

      return 'Penjualan'
    }
  },
};
</script>
